import { WhereItIs } from "../components/WhereItIs";
import { useBackState } from "@/hooks/useScrollMemory";
import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { prov, provTap } from "@/lib/provenance";
import { toast } from "sonner";
import { scanAccept, scanRefuse } from "@/lib/scanFeedback";
import { SoundToggle } from "@/components/SoundToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogHeader, DialogFooter } from "@/components/ui/dialog";
import { ScanField } from "@/components/ScanField";
import { LinePhoto } from "@/components/LinePhoto";
import { ScanTextInput } from "@/components/ScanTextInput";
import { useAuth } from "@/context/AuthContext";
import {
  ScanLine, CheckCircle2, XCircle, Truck, Volume2, VolumeX, ArrowLeft,
  PackageCheck, LogOut, SearchX, Box, MapPin, ExternalLink, PackagePlus, AlertTriangle, StickyNote, Unlink,
  Boxes, Power,
} from "lucide-react";

const LINE_PILL = {
  reserved: "bg-blue-100 text-blue-700", picked: "bg-amber-100 text-amber-700",
  removed: "bg-green-100 text-green-700", short: "bg-red-100 text-red-700",
};

const SHORT_PICK_REASONS = [
  { code: "not_on_shelf", label: "Not on shelf" },
  { code: "wrong_location", label: "Wrong location" },
  { code: "damaged", label: "Damaged" },
  { code: "already_gone", label: "Already gone" },
  { code: "other", label: "Other" },
];


export default function PickPage() {
  const { cartId: routeCartId } = useParams();
  const { has } = useAuth();
  const canSwitch = has("manage_settings");
  const [queue, setQueue] = useState([]);
  const [cart, setCart] = useState(null);
  const [mode, setMode] = useBackState("mode", "pick");          // pick | out
  const [partScan, setPartScan] = useState("");
  const [binScan, setBinScan] = useState("");
  const [last, setLast] = useState(null);
  const [detailLine, setDetailLine] = useState(null);
  const [gateMode, setGateMode] = useState("override_allowed");
  const [shortPick, setShortPick] = useState(null);   // {partId}
  const [shortReason, setShortReason] = useState(null);
  const [shortNote, setShortNote] = useState("");
  const [ovr, setOvr] = useState(null);              // pending override { code }
  const [ovrReason, setOvrReason] = useState("");
  // PICK & SHIP — staging, totes and truck scans. OFF until he says, and absent when off.
  const [staging, setStaging] = useState(null);      // {enabled, setup}
  const [toteScan, setToteScan] = useState("");
  const [truckScan, setTruckScan] = useState("");
  const [noTote, setNoTote] = useState(null);        // {code} — asking for a reason
  const [arrivePlace, setArrivePlace] = useState(""); // where a tote lands when it arrives

  // Never while a camera is live: on a tablet, focusing a box pops the keyboard over the camera view.
  const focusEl = (tid) => setTimeout(() => { if (!document.querySelector("[id$='-camera-reader'] video")) document.querySelector(`[data-testid="${tid}"]`)?.focus(); }, 60);

  const loadQueue = useCallback(async () => {
    try { const { data } = await api.get("/carts/pick-queue"); setQueue(data.carts); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);
  // keepFlash: a refresh AFTER a scan must leave that scan's result card up until the next scan.
  const loadCart = useCallback(async (id, keepFlash = false) => {
    try { const { data } = await api.get(`/carts/${id}`); setCart(data); if (!keepFlash) setLast(null); focusEl("pick-part-input"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);

  // Deep-link: arriving via /pick/:cartId opens straight ON that cart (no queue re-select).
  useEffect(() => {
    api.get("/carts/pick-gate").then(({ data }) => setGateMode(data.bin_scan_mode)).catch(() => {});
    api.get("/pick-ship/settings").then(({ data }) => setStaging(data)).catch(() => {});
    if (routeCartId) loadCart(routeCartId); else loadQueue();
  }, [routeCartId, loadCart, loadQueue]);

  const flash = (ok, payload) => {
    // HIS ONE SCAN ANSWER: green tint + accept sound, or red + buzz WITH the plain words.
    if (ok) scanAccept(payload?.part?.short_code || (payload?.removed ? "OUT" : "PICKED"));
    else scanRefuse(payload?.message || "THAT SCAN WAS REFUSED");
    setLast({ ok, ...payload });
  };

  const doPick = async (pcode, bcode, override = false, reason = "", meta = null) => {
    if (!pcode) { toast.error("Scan the part first."); return; }
    try {
      const { data } = await api.post(`/carts/${cart.id}/pick`, {
        part_scan: pcode, location_scan: bcode || undefined,
        bin_override: override, override_reason: reason || undefined,
        provenance: meta ? prov("pick_cart", meta) : provTap("pick_cart"),
      });
      if (data.ok) flash(true, { part: data.part, location: data.location, field_added: data.field_added, override: data.bin_override });
      else if (data.needs_bin && data.override_available && !override) { setOvr({ code: pcode }); setOvrReason(""); return; }
      else flash(false, { message: data.message });
    } catch (err) { flash(false, { message: formatApiErrorDetail(err.response?.data?.detail) }); }
    setPartScan(""); setBinScan(""); await loadCart(cart.id, true); focusEl("pick-part-input");
  };
  const confirmOverride = async () => { const code = ovr.code; setOvr(null); await doPick(code, "", true, ovrReason.trim()); };

  // Field-added part with no bin — pick straight from its line (no scan / no bin needed).
  const doPickLine = async (l) => {
    const token = l.qr_token || l.barcode_token || l.tag_code || l.short_code;
    try {
      const { data } = await api.post(`/carts/${cart.id}/pick`, { part_scan: token,
                                                                  provenance: provTap("pick_cart") });
      if (data.ok) flash(true, { part: data.part, location: data.location, field_added: data.field_added });
      else flash(false, { message: data.message });
    } catch (err) { flash(false, { message: formatApiErrorDetail(err.response?.data?.detail) }); }
    await loadCart(cart.id, true);
  };

  // part scanned → remember it and hop to the bin field (preserves the part→bin flow). ONE CAMERA FOR BOTH STEPS:
  // with the camera on the part box, the scan after a part is that part's bin — it stays on, part, bin, part, bin.
  const onPartScan = (code, meta) => {
    if (meta?.input === "camera" && partScan) return onBinScan(code, meta);
    setPartScan(code); focusEl("pick-bin-input");
  };
  // bin scanned → remember it and auto-confirm the pick
  const onBinScan = (code, meta) => { setBinScan(code); doPick(partScan, code, false, "", meta); };

  const doOut = async (code, meta) => {
    if (!code) return;
    try {
      const { data } = await api.post(`/carts/${cart.id}/remove`, { part_scan: code,
                                                                    provenance: prov("pick_scan_out", meta) });
      if (data.ok) { flash(true, { part: data.part, removed: true, completed: data.completed }); if (data.completed) toast.success(`${cart.cart_number} completed!`); }
      else flash(false, { message: data.message });
    } catch (err) { flash(false, { message: formatApiErrorDetail(err.response?.data?.detail) }); }
    setPartScan(""); await loadCart(cart.id, true); focusEl("out-part-input");
  };

  // ---------------- staging: scan the part into a tote (only when the switch is on)
  const doStage = async (code, meta, toteCode, reason) => {
    try {
      const { data } = await api.post("/pick-ship/stage", {
        part_scan: code, tote_scan: toteCode || undefined,
        no_tote_reason: reason || undefined,
        provenance: meta ? prov("pick_stage", meta) : provTap("pick_stage"),
      });
      if (data.ok) flash(true, { part: data.part, staged: data.tote || "no tote", message: data.message });
      else flash(false, { message: data.message });
    } catch (err) { flash(false, { message: formatApiErrorDetail(err.response?.data?.detail) }); }
    setPartScan(""); setToteScan(""); await loadCart(cart.id, true); focusEl("stage-part-input");
  };
  const onToteScan = (code, meta) => { setToteScan(code); doStage(partScan, meta, code); };

  const doLoad = async (code, meta) => {
    const truck = code || truckScan;
    if (!toteScan) { toast.error("Scan the tote first."); return; }
    try {
      const { data } = await api.post("/pick-ship/load", { tote_scan: toteScan, truck_scan: truck,
                                                           provenance: meta ? prov("pick_load", meta) : provTap("pick_load") });
      if (data.ok) flash(true, { message: data.message, loaded: true });
      else flash(false, { message: data.message });
    } catch (err) { flash(false, { message: formatApiErrorDetail(err.response?.data?.detail) }); }
    setToteScan(""); setTruckScan(""); await loadCart(cart.id, true);
  };

  // Step 4 happens at the OTHER end — the tote comes off the truck and lands somewhere.
  const doArrived = async (code, meta) => {
    try {
      const { data } = await api.post("/pick-ship/arrived", {
        tote_scan: code, place_scan: arrivePlace || undefined,
        provenance: meta ? prov("pick_arrived", meta) : provTap("pick_arrived"),
      });
      if (data.ok) flash(true, { message: data.message, loaded: true });
      else flash(false, { message: data.message });
    } catch (err) { flash(false, { message: formatApiErrorDetail(err.response?.data?.detail) }); }
    setArrivePlace("");
  };

  const toggleStaging = async (enabled) => {
    try {
      const { data } = await api.put("/pick-ship/settings", { enabled });
      setStaging((s0) => ({ ...s0, ...data }));
      toast.success(enabled ? "Staging is on — totes and truck scans are available" : "Staging is off — nothing else changed");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const cantFind = (partId) => { setShortPick({ partId }); setShortReason(null); setShortNote(""); };
  const confirmShortPick = async () => {
    const partId = shortPick?.partId; if (!partId || !shortReason) return;
    const note = shortReason === "other" ? shortNote.trim() : SHORT_PICK_REASONS.find((r) => r.code === shortReason)?.label;
    if (shortReason === "other" && note.length < 2) return;
    try { const { data } = await api.post(`/carts/${cart.id}/short-pick`, { part_id: partId, note }); setCart(data); setShortPick(null); toast.message("Flagged short — line left unfilled, part released."); await loadQueue(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // ---------- queue view ----------
  if (!cart) {
    return (
      <div data-testid="pick-page">
        <h1 className="font-display text-2xl font-bold tracking-tight mb-1 flex items-center gap-2"><Truck className="w-6 h-6 text-[#B01E28]" />Pick &amp; Pull Parts</h1>
        <p className="text-sm text-slate-500 mb-4">Carts approved and waiting to be pulled. Tap one to start scanning.</p>
        {staging && (
          <div data-testid="staging-switch-card" className={`mb-4 rounded-lg border-2 p-3 ${staging.enabled ? "border-sky-300 bg-sky-50" : "border-slate-200 bg-slate-50"}`}>
            <div className="flex items-start gap-2">
              <Power className={`w-5 h-5 shrink-0 mt-0.5 ${staging.enabled ? "text-sky-700" : "text-slate-400"}`} />
              <div className="min-w-0 flex-1">
                <div className="font-bold text-sm" data-testid="staging-switch-state">
                  Staging, totes &amp; truck scans — {staging.enabled ? "ON" : "OFF"}
                </div>
                <p className="text-xs text-slate-600 mt-0.5">
                  {staging.enabled
                    ? <>A third <b>Stage</b> button appears when you open a job: scan the part, scan the tote,
                        then scan the tote onto a truck. It is never a required step — a job can still be
                        picked and sent without it.</>
                    : <>Built and waiting. While it is off, nothing on this screen changes and no new step
                        appears anywhere.</>}
                </p>
                <div className="text-[11px] text-slate-500 mt-1" data-testid="staging-setup-counts">
                  Set up so far: {staging.setup?.bays || 0} staging bay(s) · {staging.setup?.totes || 0} tote(s) · {staging.setup?.trucks || 0} truck place(s)
                </div>
                {canSwitch && (
                  <Button data-testid="staging-switch-toggle" variant="outline"
                    onClick={() => toggleStaging(!staging.enabled)}
                    className={`mt-2 h-10 rounded-lg font-bold ${staging.enabled ? "border-slate-300 text-slate-600" : "border-sky-600 text-sky-700 hover:bg-sky-50"}`}>
                    {staging.enabled ? "Switch it off" : "Switch it on"}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
        <div className="space-y-2" data-testid="pick-queue">
          {queue.length === 0 ? <div className="border border-dashed border-slate-200 p-10 text-center text-slate-400 text-sm">Nothing to pull right now.</div> :
            queue.map((c) => (
              <button key={c.id} data-testid={`queue-${c.cart_number}`} onClick={() => loadCart(c.id)} className="w-full text-left border border-slate-200 bg-white p-4 hover:border-[#B01E28] flex items-center justify-between active:bg-slate-50">
                <div>
                  <div className="font-display text-lg font-black">{c.cart_number}</div>
                  <div className="text-xs text-slate-500">{c.assignment_type === "build" ? `Build · PO ${c.build?.po_number}` : c.assignment_type === "customer_order" ? "Customer Order" : "Internal Use"}</div>
                </div>
                <div className="text-right"><div className="font-mono text-2xl font-black text-[#B01E28]">{c.remaining}</div><div className="text-[10px] uppercase text-slate-400">to pull</div></div>
              </button>
            ))}
        </div>
      </div>
    );
  }

  // ---------- pick/scan-out view (mobile-first) ----------
  return (
    <div data-testid="pick-active" className="max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-3">
        <button data-testid="back-to-queue" onClick={() => { setCart(null); loadQueue(); }} className="inline-flex items-center gap-1 text-sm text-slate-600"><ArrowLeft className="w-4 h-4" />Queue</button>
        <SoundToggle />
      </div>

      <div className="border border-slate-200 bg-white p-3 mb-3">
        <div className="font-display text-xl font-black" data-testid="pick-cart-title">
          {cart.assignment_type === "build"
            ? <>PO {cart.build?.po_number}{cart.build?.name ? <span className="text-slate-500 font-bold"> · {cart.build.name}</span> : null}</>
            : cart.assignment_type === "customer_order"
              ? <>{cart.customer_order?.customer_name || "Customer Order"}{cart.customer_order?.order_ref ? <span className="text-slate-500 font-bold"> · {cart.customer_order.order_ref}</span> : null}</>
              : "Internal Use"}
        </div>
        <div className="text-xs text-slate-400 font-mono mt-0.5">{cart.cart_number}</div>
      </div>

      {/* Mode toggle — large touch targets. The third button only exists when staging is on. */}
      <div className={`grid ${staging?.enabled ? "grid-cols-3" : "grid-cols-2"} gap-2 mb-3`}>
        <button data-testid="mode-pick" onClick={() => { setMode("pick"); focusEl("pick-part-input"); }} className={`py-3 font-bold uppercase text-sm flex items-center justify-center gap-2 border-2 ${mode === "pick" ? "border-[#B01E28] bg-[#B01E28] text-white" : "border-slate-200 text-slate-500"}`}><PackageCheck className="w-5 h-5" />Pick</button>
        <button data-testid="mode-out" onClick={() => { setMode("out"); focusEl("out-part-input"); }} className={`py-3 font-bold uppercase text-sm flex items-center justify-center gap-2 border-2 ${mode === "out" ? "border-[#1F1F1F] bg-[#1F1F1F] text-white" : "border-slate-200 text-slate-500"}`}><LogOut className="w-5 h-5" />Scan Out</button>
        {staging?.enabled && (
          <button data-testid="mode-stage" onClick={() => { setMode("stage"); focusEl("stage-part-input"); }}
            className={`py-3 font-bold uppercase text-sm flex items-center justify-center gap-2 border-2 ${mode === "stage" ? "border-sky-700 bg-sky-700 text-white" : "border-slate-200 text-slate-500"}`}>
            <Boxes className="w-5 h-5" />Stage
          </button>
        )}
      </div>

      {/* Sticky scan area — shared ScanField: camera + scan-gun + manual + QR-URL normalize */}
      <div className="sticky top-2 z-10 bg-white border-2 border-slate-200 p-3 mb-3 shadow-sm">
        {mode === "pick" ? (
          <div className="space-y-3">
            <div>
              <label className="text-[11px] font-bold uppercase text-slate-500 flex items-center gap-1"><ScanLine className="w-3.5 h-3.5 text-[#B01E28]" />1 · Scan part</label>
              {partScan && <div data-testid="pick-part-captured" className="text-xs font-mono text-emerald-700 mt-1">✓ {partScan}</div>}
              <div className="mt-1"><ScanField testid="pick-part" autoFocus={false} submitLabel="Next" placeholder="Scan part tag" onScan={onPartScan} continuous paused={!!ovr || !!shortPick} /></div>
              {partScan && <div data-testid="pick-camera-next" className="text-[11px] text-slate-500 mt-1">Camera on? The next scan is its bin.</div>}
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase text-slate-500 flex items-center gap-1"><Box className="w-3.5 h-3.5 text-[#B01E28]" />2 · Scan bin</label>
              {binScan && <div data-testid="pick-bin-captured" className="text-xs font-mono text-emerald-700 mt-1">✓ {binScan}</div>}
              <div className="mt-1"><ScanField testid="pick-bin" autoFocus={false} submitLabel="Pick" placeholder="Scan bin code" onScan={onBinScan} /></div>
            </div>
            <Button data-testid="pick-confirm" type="button" onClick={() => doPick(partScan, binScan)} className="w-full h-12 rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-base">Confirm pick</Button>
            {gateMode !== "hard" && (
              <Button data-testid="pick-override" type="button" variant="outline" disabled={!partScan}
                onClick={() => { setOvr({ code: partScan }); setOvrReason(""); }}
                className="w-full h-11 rounded-lg border-amber-300 text-amber-700 hover:bg-amber-50">
                <Unlink className="w-4 h-4 mr-1.5" />Pick without bin scan (logged)
              </Button>
            )}
            {gateMode === "hard" && <div className="text-[11px] text-center text-slate-400">Bin scan required (hard-enforced)</div>}
          </div>
        ) : mode === "stage" ? (
          <div className="space-y-3" data-testid="stage-panel">
            <div>
              <label className="text-[11px] font-bold uppercase text-slate-500 flex items-center gap-1"><ScanLine className="w-3.5 h-3.5 text-sky-700" />1 · Scan a picked part</label>
              {partScan && <div data-testid="stage-part-captured" className="text-xs font-mono text-emerald-700 mt-1">✓ {partScan}</div>}
              <div className="mt-1"><ScanField testid="stage-part" autoFocus={false} submitLabel="Next" placeholder="Scan part tag" onScan={(c) => { setPartScan(c); focusEl("stage-tote-input"); }} /></div>
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase text-slate-500 flex items-center gap-1"><Boxes className="w-3.5 h-3.5 text-sky-700" />2 · Scan the tote</label>
              {toteScan && <div data-testid="stage-tote-captured" className="text-xs font-mono text-emerald-700 mt-1">✓ {toteScan}</div>}
              <div className="mt-1"><ScanField testid="stage-tote" autoFocus={false} submitLabel="Stage" placeholder="Scan tote code" onScan={onToteScan} /></div>
            </div>
            {/* NOTHING CAN BLOCK A JOB — no tote to hand is a reason, not a stop. */}
            <Button data-testid="stage-no-tote" type="button" variant="outline" disabled={!partScan}
              onClick={() => setNoTote({ code: partScan })}
              className="w-full h-11 rounded-lg border-amber-300 text-amber-700 hover:bg-amber-50">
              <AlertTriangle className="w-4 h-4 mr-1.5" />No tote — leave it on the cart (logged)
            </Button>
            <div className="border-t border-slate-200 pt-3">
              <label className="text-[11px] font-bold uppercase text-slate-500 flex items-center gap-1"><Truck className="w-3.5 h-3.5 text-sky-700" />3 · Scan the tote onto a truck</label>
              <p className="text-[11px] text-slate-500 mt-0.5">Scan the tote above first, then the truck — one scan moves everything in it.</p>
              <div className="mt-1"><ScanField testid="stage-truck" autoFocus={false} submitLabel="Load" placeholder="Scan truck code" onScan={doLoad} /></div>
            </div>
            <div className="border-t border-slate-200 pt-3">
              <label className="text-[11px] font-bold uppercase text-slate-500 flex items-center gap-1"><PackageCheck className="w-3.5 h-3.5 text-sky-700" />4 · Tote arrived — off the truck</label>
              <p className="text-[11px] text-slate-500 mt-0.5">At the other end: type where it lands (optional), then scan the tote.</p>
              <ScanTextInput testid="stage-arrive-place" value={arrivePlace} onChange={setArrivePlace}
                placeholder="Shelf or bay code (optional)" className="mt-1" inputClassName="h-11 text-base" />
              <div className="mt-1"><ScanField testid="stage-arrive" autoFocus={false} submitLabel="Arrived" placeholder="Scan tote code" onScan={doArrived} /></div>
            </div>
          </div>
        ) : (
          <div>
            <label className="text-[11px] font-bold uppercase text-slate-500 flex items-center gap-1"><LogOut className="w-3.5 h-3.5 text-[#1F1F1F]" />Scan a PICKED part to remove from inventory</label>
            <div className="mt-1"><ScanField testid="out-part" submitLabel="Scan out" placeholder="Scan part to remove" onScan={doOut} continuous /></div>
          </div>
        )}
      </div>

      {/* Big flash */}
      {last && (
        <div data-testid="pick-flash" className={`border-2 p-5 text-center mb-3 ${last.ok ? "border-emerald-500 bg-emerald-50" : "border-red-500 bg-red-50"}`}>
          {last.ok ? <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-600" /> : <XCircle className="w-12 h-12 mx-auto text-red-600" />}
          {last.ok ? (
            <div className="mt-2">
              <div className="flex justify-center mb-2"><LinePhoto fileId={last.part?.photo} alt={last.part?.short_code} className="w-24 h-24" testid="pick-flash-photo" /></div>
              <div className="font-display text-2xl font-black">{last.part?.short_code}</div>
              <div className="text-sm text-slate-700">{last.part?.name} · {last.part?.brand}</div>
              <div className="text-xs text-emerald-700 mt-1">{last.removed ? <span className="font-mono">{last.completed ? "REMOVED · CART COMPLETE" : "REMOVED"}</span>
                : last.override ? <span className="font-mono">PICKED · NO BIN (logged)</span>
                  : last.location ? <span className="inline-flex items-center gap-1 flex-wrap justify-center"><span className="font-mono">PICKED →</span><WhereItIs location={last.location} size="sm" testid="pick-flash-where" /></span>
                    : <span className="font-mono">PICKED · field-added (no bin)</span>}</div>
            </div>
          ) : <div className="mt-2 font-mono text-sm text-red-700">{last.message}</div>}
        </div>
      )}

      {/* NO TOTE — a reason, never a stop */}
      <Dialog open={!!noTote} onOpenChange={(v) => !v && setNoTote(null)}>
        <DialogContent className="rounded-xl" data-testid="no-tote-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">No tote for this one?</DialogTitle>
            <DialogDescription>
              That is fine — it stays on the cart and the job carries on. Tell me why in a word or two,
              so the office can see how often it happens.
            </DialogDescription>
          </DialogHeader>
          <Input data-testid="no-tote-reason" autoFocus placeholder="e.g. too big for a tote · all totes in use"
            onChange={(e) => setOvrReason(e.target.value)} className="h-12 text-base" />
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-lg h-11" data-testid="no-tote-cancel" onClick={() => setNoTote(null)}>Back</Button>
            <Button data-testid="no-tote-confirm" className="rounded-lg h-11 bg-amber-600 hover:bg-amber-700 font-bold"
              onClick={() => { const c = noTote.code; const r = ovrReason.trim(); setNoTote(null); setOvrReason(""); doStage(c, null, "", r); }}>
              Stage it without a tote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lines */}
      <div className="border border-slate-200 bg-white" data-testid="pick-lines">
        {cart.lines.map((l) => (
          <div key={l.part_id} className="flex items-center justify-between gap-2 px-3 py-2.5 border-b border-slate-100">
            <button type="button" data-testid={`pick-line-photo-open-${l.short_code}`} onClick={() => setDetailLine(l)}
              title={`Open ${l.short_code}`} className="shrink-0 rounded hover:ring-2 hover:ring-[#E0781A]/40 transition-shadow">
              <LinePhoto fileId={l.photo} alt={l.short_code} className="w-12 h-12" testid={`pick-line-photo-${l.short_code}`} />
            </button>
            <button data-testid={`pick-line-open-${l.short_code}`} onClick={() => setDetailLine(l)} className="min-w-0 text-left flex-1 mr-2">
              <div className="font-mono text-sm font-bold truncate flex items-center gap-1">{l.short_code} <span className="font-normal text-slate-500">{l.name}</span>{l.field_added && <span className="text-[9px] font-sans font-bold uppercase bg-amber-100 text-amber-700 px-1 rounded">field-add</span>}</div>
              <WhereItIs location={l.location} size="sm" testid={`pick-line-where-${l.short_code}`}
                fallback={l.field_added ? "No location — field-added" : "—"} />
              {l.site && <div data-testid={`line-site-${l.short_code}`} className="text-[10px] text-slate-400 flex items-center gap-1 truncate"><Box className="w-3 h-3 shrink-0" />Site: {l.site}</div>}
              {l.lot?.notes && <div data-testid={`line-lot-notes-${l.short_code}`} className="text-[10px] text-[#7C5C00] bg-amber-50 border border-amber-100 rounded px-1.5 py-0.5 flex items-start gap-1 mt-0.5"><StickyNote className="w-3 h-3 shrink-0 mt-px" /><span className="truncate">{l.lot.title ? `${l.lot.title}: ` : ""}{l.lot.notes}</span></div>}
              {l.ship_gaps && <div data-testid={`ship-gaps-${l.short_code}`} className="text-[10px] text-[#B01E28] flex items-center gap-1 mt-0.5"><AlertTriangle className="w-3 h-3 shrink-0" />Not ship-ready: {l.ship_gaps.join(", ")}</div>}
            </button>
            <div className="flex items-center gap-2 shrink-0">
              <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 ${LINE_PILL[l.state]}`}>{l.state}</span>
              {/* Staged is a NOTE on a picked part, never a state — so it reads beside the pill. */}
              {l.staged_container_code && (
                <span data-testid={`line-tote-${l.short_code}`} className="text-[10px] font-mono uppercase px-1.5 py-0.5 bg-sky-100 text-sky-800 inline-flex items-center gap-1">
                  <Boxes className="w-3 h-3" />{l.staged_container_code}{l.on_truck_code ? ` → ${l.on_truck_code}` : ""}
                </span>
              )}
              {l.staged_without_tote && (
                <span data-testid={`line-no-tote-${l.short_code}`} className="text-[10px] font-mono uppercase px-1.5 py-0.5 bg-amber-100 text-amber-800">no tote</span>
              )}
              {l.state === "reserved" && l.field_added && <button data-testid={`pick-line-${l.short_code}`} onClick={() => doPickLine(l)} className="inline-flex items-center gap-1 text-xs text-white bg-[#E0781A] hover:bg-[#C76714] px-3 py-2 rounded"><PackagePlus className="w-3.5 h-3.5" />Pick</button>}
              {l.state === "reserved" && <button data-testid={`cant-find-${l.short_code}`} onClick={() => cantFind(l.part_id)} className="inline-flex items-center gap-1 text-xs text-red-600 border border-red-200 px-3 py-2 rounded"><SearchX className="w-3.5 h-3.5" />Can't find</button>}
            </div>
          </div>
        ))}
      </div>

      {/* Tap-into-part detail — picker confirms the item + sees where it lives */}
      <Dialog open={!!detailLine} onOpenChange={(o) => { if (!o) setDetailLine(null); }}>
        <DialogContent data-testid="pick-line-detail" className="rounded-xl max-w-sm">
          {detailLine && (
            <div>
              <DialogTitle className="font-mono">{detailLine.short_code}</DialogTitle>
              <DialogDescription className="sr-only">Part detail — location and full record.</DialogDescription>
              {detailLine.photo && <div className="my-3"><LinePhoto fileId={detailLine.photo} alt={detailLine.short_code} className="w-full h-48" testid="pick-line-detail-photo" /></div>}
              <div className="text-sm font-semibold text-slate-900">{detailLine.name || "—"}</div>
              <div className="text-xs text-slate-500 mb-3">{detailLine.brand || ""}</div>
              <div data-testid="pick-line-detail-location" className="flex items-start gap-2 text-sm bg-slate-50 border border-slate-200 rounded-lg p-3">
                <MapPin className="w-4 h-4 mt-0.5 text-[#B01E28] shrink-0" />
                <div><div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Location{detailLine.site ? ` · ${detailLine.site}` : ""}</div>
                  <WhereItIs location={detailLine.location} size="md" testid="pick-detail-where"
                    fallback={detailLine.field_added ? "No location — field-added (ship now, complete later)" : "—"} /></div>
              </div>
              {detailLine.lot?.notes && (
                <div data-testid="pick-line-detail-lot-notes" className="mt-3 flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <StickyNote className="w-4 h-4 mt-0.5 text-[#B8860B] shrink-0" />
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wide text-amber-700">Dismantler notes{detailLine.lot.title ? ` · ${detailLine.lot.title}` : ""}</div>
                    <div className="text-amber-900 whitespace-pre-wrap">{detailLine.lot.notes}</div>
                  </div>
                </div>
              )}
              <a href={`/parts/${detailLine.part_id}`} target="_blank" rel="noopener noreferrer" data-testid="pick-line-full-record" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#E0781A] hover:underline">
                <ExternalLink className="w-4 h-4" />View full part record
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Acknowledged bin override — deliberate, logged (who/when/why), hidden when kill-switch is hard */}
      <Dialog open={!!ovr} onOpenChange={(o) => { if (!o) setOvr(null); }}>
        <DialogContent data-testid="pick-override-dialog" className="rounded-xl sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Unlink className="w-5 h-5 text-amber-600" />Pick without a bin scan?</DialogTitle>
            <DialogDescription>This part isn't confirmed to a bin. Allowed during load-in, but it's <b>logged</b> (who · when) against your name. An admin can switch this off once the yard is put away.</DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-xs font-bold uppercase text-slate-500">Reason (optional)</label>
            <Input data-testid="pick-override-reason" value={ovrReason} onChange={(e) => setOvrReason(e.target.value)} placeholder="Default: not binned yet — only note if unusual" className="rounded-lg mt-1" />
          </div>
          <DialogFooter>
            <Button data-testid="pick-override-confirm" onClick={confirmOverride} className="rounded-lg bg-amber-600 hover:bg-amber-700 text-white">Pick without bin — I acknowledge</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Short-pick — floor/tablet: one-tap presets, no browser prompt box */}
      <Dialog open={!!shortPick} onOpenChange={(v) => !v && setShortPick(null)}>
        <DialogContent className="rounded-xl border-slate-200 sm:max-w-md" data-testid="short-pick-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Can't find it — why?</DialogTitle>
            <DialogDescription>The line is left unfilled and the part is released back to Available.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-2">
            {SHORT_PICK_REASONS.map((r) => (
              <button key={r.code} data-testid={`short-pick-reason-${r.code}`} onClick={() => setShortReason(r.code)}
                className={`rounded-full px-4 py-2.5 font-semibold text-sm border-2 transition-colors ${shortReason === r.code ? "border-[#B01E28] bg-red-50 text-[#B01E28]" : "border-slate-200 text-slate-600 hover:border-[#B01E28]"}`}>{r.label}</button>
            ))}
          </div>
          {shortReason === "other" && (
            <input data-testid="short-pick-note" value={shortNote} onChange={(e) => setShortNote(e.target.value)}
              placeholder="What happened?" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-lg" onClick={() => setShortPick(null)} data-testid="short-pick-cancel">Cancel</Button>
            <Button data-testid="short-pick-confirm" disabled={!shortReason || (shortReason === "other" && shortNote.trim().length < 2)} onClick={confirmShortPick} className="rounded-lg bg-[#B01E28] hover:bg-red-800 text-white font-bold">Flag short</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
